import React, { useState, useMemo, useEffect } from 'react'; // Added useEffect
import { useNavigate } from 'react-router-dom';
import { ArrowUp, ArrowDown, Download, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
// --- 1. IMPORT DATE FUNCTIONS ---
import { startOfDay, endOfDay, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths, startOfYear, subDays, format } from 'date-fns';
import { formatCurrency, formatDate } from '../utils/formatters';
import { CSVLink } from 'react-csv';
import { neutralizeCsvRows } from '../utils/csvExport'; // s9csv-4e1b7c28
// --- ADD FIRESTORE IMPORTS ---
import { db } from '../firebase/config';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { canSeeLaborCost, rowsWithCostRates } from '../utils/jobCosts'; // s6costing-4d8a2f63
import { useOrgJobCosts } from '../hooks/useJobCosts'; // s6costing-4d8a2f63


// Helper to format date for input[type=date]
const toDateInput = (date) => date.toISOString().split('T')[0];

// --- 1. CHANGE PROPS ---
const ProfitabilityReport = ({ currentUserProfile }) => {
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'descending' });
    const [expandedJobId, setExpandedJobId] = useState(null);

    const toggleExpand = (jobId) => {
        setExpandedJobId(prev => prev === jobId ? null : jobId);
    };
    
    // --- 2. ADD STATE FOR DATA AND LOADING ---
    const [jobs, setJobs] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // --- END ADD STATE ---

    const [statusFilter, setStatusFilter] = useState('Completed');
    const statusOptions = ['Completed', 'All Statuses', 'Scheduled', 'In Progress', 'Canceled'];
    const navigate = useNavigate();

    // --- 2. ADD STATE FOR DATE FILTERING ---
    const [dateRangeOption, setDateRangeOption] = useState('All Time');
    const [customStartDate, setCustomStartDate] = useState(toDateInput(subMonths(new Date(), 3)));
    const [customEndDate, setCustomEndDate] = useState(toDateInput(new Date()));

    // --- 3. ADD useEffect to fetch all data ---
    // Uses direct onSnapshot listeners so the UI updates immediately when any
    // data changes — avoids the race condition caused by onSnapshot -> getDocs.
    useEffect(() => {
        if (!currentUserProfile?.organizationId) {
            setIsLoading(false);
            return;
        }
        const orgId = currentUserProfile.organizationId;

        const jobsRef = collection(db, `organizations/${orgId}/jobs`);
        const recurringJobsRef = collection(db, `organizations/${orgId}/recurringJobs`);
        const invoicesRef = collection(db, `organizations/${orgId}/invoices`);
        const expensesRef = collection(db, `organizations/${orgId}/expenses`);

        // Track which collections have loaded so we only clear isLoading once all four are ready
        const loaded = { jobs: false, recurring: false, invoices: false, expenses: false };
        const checkAllLoaded = () => {
            if (Object.values(loaded).every(Boolean)) setIsLoading(false);
        };

        // Jobs and recurringJobs are merged into a single state value.
        // We keep separate refs so each listener can update its slice independently.
        let latestJobs = [];
        let latestRecurring = [];

        const unsubJobs = onSnapshot(jobsRef, (snap) => {
            latestJobs = snap.docs.map(d => ({ ...d.data(), id: d.id })); // s6costing-docid: doc id wins over a stale stored `id` field
            setJobs([...latestJobs, ...latestRecurring]);
            loaded.jobs = true;
            checkAllLoaded();
        }, (err) => { console.error("Jobs snapshot error:", err); setIsLoading(false); });

        const unsubRecurring = onSnapshot(recurringJobsRef, (snap) => {
            latestRecurring = snap.docs.map(d => ({ ...d.data(), id: d.id })); // s6costing-docid: doc id wins over a stale stored `id` field
            setJobs([...latestJobs, ...latestRecurring]);
            loaded.recurring = true;
            checkAllLoaded();
        }, (err) => { console.error("Recurring snapshot error:", err); setIsLoading(false); });

        const unsubInvoices = onSnapshot(invoicesRef, (snap) => {
            setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            loaded.invoices = true;
            checkAllLoaded();
        }, (err) => { console.error("Invoices snapshot error:", err); setIsLoading(false); });

        const unsubExpenses = onSnapshot(expensesRef, (snap) => {
            setExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            loaded.expenses = true;
            checkAllLoaded();
        }, (err) => { console.error("Expenses snapshot error:", err); setIsLoading(false); });

        return () => {
            unsubJobs();
            unsubRecurring();
            unsubInvoices();
            unsubExpenses();
        };
    }, [currentUserProfile]);
    // --- END useEffect ---

    // --- 3. CALCULATE THE DATE RANGE BASED ON THE SELECTED OPTION ---
    const dateRange = useMemo(() => {
        const now = new Date();
        switch (dateRangeOption) {
            case 'All Time': return { start: null, end: null };
            case 'Today': return { start: startOfDay(now), end: endOfDay(now) };
            case 'Yesterday': 
                const yesterday = subDays(now, 1);
                return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
            case 'This Week': return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
            case 'Last Week':
                const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
                return { start: lastWeekStart, end: endOfWeek(lastWeekStart, { weekStartsOn: 1 }) };
            case 'This Month': return { start: startOfMonth(now), end: endOfMonth(now) };
            case 'Last Month':
                const lastMonthStart = startOfMonth(subMonths(now, 1));
                return { start: lastMonthStart, end: endOfMonth(lastMonthStart) };
            case 'Last 3 Months': return { start: startOfDay(subMonths(now, 3)), end: endOfDay(now) };
            case 'Last 6 Months': return { start: startOfDay(subMonths(now, 6)), end: endOfDay(now) };
            case 'Last 12 Months': return { start: startOfDay(subMonths(now, 12)), end: endOfDay(now) };
            case 'YTD': return { start: startOfYear(now), end: endOfDay(now) };
            case 'Custom':
                const start = new Date(`${customStartDate}T00:00:00`);
                const end = new Date(`${customEndDate}T00:00:00`);
                return { start: startOfDay(start), end: endOfDay(end) };
            default: return { start: null, end: null };
        }
    }, [dateRangeOption, customStartDate, customEndDate]);

    // s6costing-4d8a2f63 — S6b: labour cost rates come from jobCosts (Owner/Admin/Manager only),
    // falling back to a not-yet-migrated row's own rate. (This legacy screen also lists recurring
    // TEMPLATES, which have no jobCosts doc; their template rates are stripped by the migration.)
    const { ratesByJob } = useOrgJobCosts(currentUserProfile?.organizationId, canSeeLaborCost(currentUserProfile));

    const reportData = useMemo(() => {
        // --- 4. UPDATE MAIN CALCULATION TO INCLUDE DATE FILTERING ---
        let filteredJobs = jobs;

        // First, filter by status
        if (statusFilter !== 'All Statuses') {
            filteredJobs = filteredJobs.filter(job => job.status === statusFilter);
        }

        // Second, filter by date range (if not "All Time")
        if (dateRange.start && dateRange.end) {
            filteredJobs = filteredJobs.filter(job => {
                const jobDate = job.startAt?.toDate();
                return jobDate && jobDate >= dateRange.start && jobDate <= dateRange.end;
            });
        }

        let processedJobs = filteredJobs.map(job => {
            const relatedInvoice = invoices.find(inv => inv.jobId === job.id);
            const revenue = relatedInvoice ? parseFloat(relatedInvoice.total) || 0 : 0;

            const costRows = rowsWithCostRates(job.jobCosting?.laborEntries, ratesByJob[job.id]); // s6costing-4d8a2f63
            const laborCost = costRows.reduce((total, entry) => {
                return total + (parseFloat(entry.hours) || 0) * (parseFloat(entry.rate) || 0);
            }, 0) || 0;
            
            const materialCost = expenses
                .filter(exp => exp.jobId === job.id)
                .reduce((total, exp) => total + (parseFloat(exp.amount) || 0), 0);

            const totalCost = materialCost + laborCost;
            const profit = revenue - totalCost;
            const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;

            // Capture job expenses (already filtered above for materialCost)
            const jobExpenses = expenses.filter(exp => exp.jobId === job.id);

            return {
                id: job.id,
                date: job.startAt?.toDate(),
                title: job.title,
                clientName: job.clientName,
                jobNumber: job.jobNumber,
                revenue,
                laborCost,
                materialCost,
                totalCost,
                profit,
                profitMargin,
                // Detail data for expanded row — no extra Firestore reads needed
                laborEntries: costRows,
                jobExpenses,
                invoice: relatedInvoice || null,
            };
        });

        if (sortConfig.key) {
            processedJobs.sort((a, b) => {
                const aValue = a[sortConfig.key] || 0;
                const bValue = b[sortConfig.key] || 0;
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return processedJobs;
    }, [jobs, invoices, expenses, sortConfig, statusFilter, dateRange, ratesByJob]); // <-- Add dateRange to dependency array

    const csvHeaders = [
        { label: "Job #", key: "jobNumber" },
        { label: "Completed Date", key: "date" },
        { label: "Job Title", key: "title" },
        { label: "Customer", key: "clientName" },
        { label: "Revenue", key: "revenue" },
        { label: "Labor Cost", key: "laborCost" },
        { label: "Expenses", key: "materialCost" },
        { label: "Total Cost", key: "totalCost" },
        { label: "Profit", key: "profit" },
        { label: "Margin (%)", key: "profitMargin" },
    ];

    const csvData = useMemo(() => reportData.map(item => ({
        ...item,
        date: item.date ? format(item.date, 'yyyy-MM-dd') : 'N/A',
        revenue: item.revenue.toFixed(2),
        laborCost: item.laborCost.toFixed(2),
        materialCost: item.materialCost.toFixed(2),
        totalCost: item.totalCost.toFixed(2),
        profit: item.profit.toFixed(2),
        profitMargin: item.profitMargin.toFixed(1),
    })), [reportData]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="inline-block w-4"></span>;
        return sortConfig.direction === 'ascending' 
            ? <ArrowUp className="inline-block ml-1 h-4 w-4" /> 
            : <ArrowDown className="inline-block ml-1 h-4 w-4" />;
    };
    
    // --- 4. ADD LOADING CHECK ---
    if (isLoading) {
        return <p className="text-center mt-8">Loading report data...</p>;
    }

    return (
        <div className="p-6 md:p-10">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Profitability Report</h1>
            
            {/* --- 5. ADD THE NEW FILTER UI --- */}
            <div className="flex flex-wrap items-end gap-4 p-4 border rounded-lg bg-gray-50 mb-6">
                <div className="flex-grow min-w-[150px]">
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="mt-1 w-full p-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                        {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                </div>
                <div className="flex-grow min-w-[150px]">
                    <label className="block text-sm font-medium text-gray-700">Date Range (Job Start Date)</label>
                    <select value={dateRangeOption} onChange={(e) => setDateRangeOption(e.target.value)} className="mt-1 w-full p-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                        <option>All Time</option>
                        <option>Today</option>
                        <option>Yesterday</option>
                        <option>This Week</option>
                        <option>Last Week</option>
                        <option>This Month</option>
                        <option>Last Month</option>
                        <option>Last 3 Months</option>
                        <option>Last 6 Months</option>
                        <option>Last 12 Months</option>
                        <option>YTD</option>
                        <option>Custom</option>
                    </select>
                </div>
                {dateRangeOption === 'Custom' && (
                    <>
                        <div className="flex-grow min-w-[150px]">
                            <label className="block text-sm font-medium text-gray-700">Start Date</label>
                            <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="mt-1 w-full p-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                        </div>
                        <div className="flex-grow min-w-[150px]">
                            <label className="block text-sm font-medium text-gray-700">End Date</label>
                            <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="mt-1 w-full p-2 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                        </div>
                    </>
                )}
                <div className="flex-shrink-0">
                    <CSVLink 
                        data={neutralizeCsvRows(csvData)}
                        headers={csvHeaders}
                        filename={`profitability-report-${new Date().toISOString().split('T')[0]}.csv`}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700"
                    >
                        <Download size={16} />
                        Export
                    </CSVLink>
                </div>
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs uppercase bg-gray-50">
                        <tr>
                            {/* Expand toggle column */}
                            <th className="w-8 px-3 py-3"></th>
                            <th className="px-6 py-3 text-right cursor-pointer whitespace-nowrap" onClick={() => requestSort('jobNumber')}>Job # {getSortIndicator('jobNumber')}</th>
                            <th className="px-6 py-3 cursor-pointer whitespace-nowrap" onClick={() => requestSort('date')}>Date {getSortIndicator('date')}</th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('title')}>Job Title {getSortIndicator('title')}</th>
                            <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('clientName')}>Customer {getSortIndicator('clientName')}</th>
                            <th className="px-6 py-3 text-right cursor-pointer" onClick={() => requestSort('revenue')}>Revenue {getSortIndicator('revenue')}</th>
                            <th className="px-6 py-3 text-right cursor-pointer" onClick={() => requestSort('laborCost')}>Labor {getSortIndicator('laborCost')}</th>
                            <th className="px-6 py-3 text-right cursor-pointer" onClick={() => requestSort('materialCost')}>Materials {getSortIndicator('materialCost')}</th>
                            <th className="px-6 py-3 text-right cursor-pointer" onClick={() => requestSort('totalCost')}>Total Cost {getSortIndicator('totalCost')}</th>
                            <th className="px-6 py-3 text-right cursor-pointer" onClick={() => requestSort('profit')}>Net Profit {getSortIndicator('profit')}</th>
                            <th className="px-6 py-3 text-right cursor-pointer" onClick={() => requestSort('profitMargin')}>Margin {getSortIndicator('profitMargin')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reportData.map(item => {
                            const isExpanded = expandedJobId === item.id;
                            return (
                                <React.Fragment key={item.id}>
                                    {/* ── SUMMARY ROW ── */}
                                    <tr
                                        className={`border-b cursor-pointer transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-gray-50'}`}
                                        onClick={() => toggleExpand(item.id)}
                                    >
                                        <td className="w-8 px-3 py-4 text-gray-400">
                                            {isExpanded
                                                ? <ChevronDown className="h-4 w-4 text-indigo-500" />
                                                : <ChevronRight className="h-4 w-4" />}
                                        </td>
                                        <td className="px-6 py-4 text-right font-mono text-sm font-semibold text-gray-700">
                                            {item.jobNumber ? `#${String(item.jobNumber).replace(/^#/, '')}` : '—'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">{formatDate(item.date)}</td>
                                        <td className="px-6 py-4 font-medium text-gray-900">{item.title}</td>
                                        <td className="px-6 py-4">{item.clientName}</td>
                                        <td className="px-6 py-4 text-right">{formatCurrency(item.revenue)}</td>
                                        <td className="px-6 py-4 text-right">{formatCurrency(item.laborCost)}</td>
                                        <td className="px-6 py-4 text-right">{formatCurrency(item.materialCost)}</td>
                                        <td className="px-6 py-4 text-right font-semibold">{formatCurrency(item.totalCost)}</td>
                                        <td className={`px-6 py-4 text-right font-semibold ${item.profit < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {formatCurrency(item.profit)}
                                        </td>
                                        <td className={`px-6 py-4 text-right font-semibold ${item.profit < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {item.profitMargin.toFixed(1)}%
                                        </td>
                                    </tr>

                                    {/* ── EXPANDED DETAIL ROW ── */}
                                    {isExpanded && (
                                        <tr className="bg-slate-50 border-b border-slate-200">
                                            <td colSpan={11} className="px-6 py-5">
                                                <div className="border-l-4 border-indigo-400 pl-4 space-y-5">

                                                    {/* ── LABOR BREAKDOWN ── */}
                                                    <div>
                                                        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                                                            Labor Breakdown
                                                        </h4>
                                                        {item.laborEntries.length > 0 ? (
                                                            <table className="w-full text-sm">
                                                                <thead>
                                                                    <tr className="text-xs text-gray-500 border-b border-slate-200">
                                                                        <th className="text-left pb-1 font-medium pr-6">Technician</th>
                                                                        <th className="text-right pb-1 font-medium pr-6">Clocked Hrs</th>
                                                                        <th className="text-right pb-1 font-medium pr-6">Allocated Hrs</th>
                                                                        <th className="text-right pb-1 font-medium pr-6">Rate</th>
                                                                        <th className="text-right pb-1 font-medium">Labor Cost</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.laborEntries.map((entry, i) => {
                                                                        const hrs = parseFloat(entry.hours) || 0;
                                                                        const rate = parseFloat(entry.rate) || 0;
                                                                        const allocated = parseFloat(entry.allocatedHours) || null;
                                                                        const entryCost = hrs * rate;
                                                                        const overBudget = allocated !== null && hrs > allocated;
                                                                        return (
                                                                            <tr key={i} className="border-b border-slate-100 last:border-0">
                                                                                <td className="py-1.5 pr-6 text-gray-700">{entry.name || '—'}</td>
                                                                                <td className={`py-1.5 pr-6 text-right font-mono ${overBudget ? 'text-amber-600 font-semibold' : 'text-gray-700'}`}>
                                                                                    {hrs.toFixed(2)}h
                                                                                    {overBudget && <span className="ml-1 text-xs text-amber-500">▲</span>}
                                                                                </td>
                                                                                <td className="py-1.5 pr-6 text-right font-mono text-gray-400">
                                                                                    {allocated !== null ? `${allocated.toFixed(2)}h` : '—'}
                                                                                </td>
                                                                                <td className="py-1.5 pr-6 text-right font-mono text-gray-500">
                                                                                    {rate > 0 ? `$${rate.toFixed(2)}/hr` : '—'}
                                                                                </td>
                                                                                <td className="py-1.5 text-right font-semibold text-gray-800">
                                                                                    {formatCurrency(entryCost)}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        ) : (
                                                            <p className="text-sm text-gray-400 italic">No labor entries recorded.</p>
                                                        )}
                                                    </div>

                                                    {/* ── EXPENSES / MATERIALS ── */}
                                                    <div>
                                                        <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                                                            Expenses &amp; Materials
                                                        </h4>
                                                        {item.jobExpenses.length > 0 ? (
                                                            <table className="w-full text-sm">
                                                                <thead>
                                                                    <tr className="text-xs text-gray-500 border-b border-slate-200">
                                                                        <th className="text-left pb-1 font-medium pr-6">Description</th>
                                                                        <th className="text-left pb-1 font-medium pr-6">Category</th>
                                                                        <th className="text-left pb-1 font-medium pr-6">Date</th>
                                                                        <th className="text-right pb-1 font-medium">Amount</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.jobExpenses.map((exp, i) => (
                                                                        <tr key={i} className="border-b border-slate-100 last:border-0">
                                                                            <td className="py-1.5 pr-6 text-gray-700">{exp.description || exp.name || '—'}</td>
                                                                            <td className="py-1.5 pr-6 text-gray-500">{exp.category || '—'}</td>
                                                                            <td className="py-1.5 pr-6 text-gray-400 text-xs">
                                                                                {exp.date ? formatDate(exp.date?.toDate ? exp.date.toDate() : new Date(exp.date)) : '—'}
                                                                            </td>
                                                                            <td className="py-1.5 text-right font-semibold text-gray-800">
                                                                                {formatCurrency(parseFloat(exp.amount) || 0)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        ) : (
                                                            <p className="text-sm text-gray-400 italic">No expenses recorded for this job.</p>
                                                        )}
                                                    </div>

                                                    {/* ── INVOICE + VIEW JOB LINK ── */}
                                                    <div className="flex items-center justify-between pt-1">
                                                        <div className="text-sm text-gray-600">
                                                            {item.invoice ? (
                                                                <span>
                                                                    <span className="font-medium text-gray-700">Invoice:</span>
                                                                    {' '}
                                                                    {item.invoice.invoiceNumber
                                                                        ? `#${String(item.invoice.invoiceNumber).replace(/^#/, '')}`
                                                                        : 'Linked'}
                                                                    {' · '}
                                                                    <span className="font-semibold">{formatCurrency(parseFloat(item.invoice.total) || 0)}</span>
                                                                    {item.invoice.status && (
                                                                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                            item.invoice.status === 'Paid'
                                                                                ? 'bg-green-100 text-green-700'
                                                                                : item.invoice.status === 'Sent'
                                                                                    ? 'bg-blue-100 text-blue-700'
                                                                                    : 'bg-gray-100 text-gray-600'
                                                                        }`}>
                                                                            {item.invoice.status}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-400 italic">No invoice linked.</span>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${item.id}`); }}
                                                            className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                                                        >
                                                            View Full Job <ExternalLink className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>

                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                 {reportData.length === 0 && <p className="text-center py-10 text-gray-500">No jobs match the selected filters.</p>}
            </div>
        </div>
    );
};

export default ProfitabilityReport;